import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiEdit, FiMail, FiPhone, FiCalendar, FiBriefcase } from "react-icons/fi";
import adminApi from "../../api/adminApi";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import Select from "../../components/Select/Select";
import FormInput from "../../components/FormInput/FormInput";
import "./HiringRequestDetail.css";

const HiringRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get(`/hiring-requests/${id}`);
      setRequest(response.data);
      setNewStatus(response.data.status);
      setNotes(response.data.notes || "");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load hiring request");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    setUpdateLoading(true);
    try {
      await adminApi.patch(`/hiring-requests/${id}`, { status: newStatus });
      await loadRequest();
      setShowStatusModal(false);
    } catch (err) {
      console.error("Status update failed:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleNotesUpdate = async () => {
    setUpdateLoading(true);
    try {
      await adminApi.patch(`/hiring-requests/${id}`, { notes });
      await loadRequest();
      setShowNotesModal(false);
    } catch (err) {
      console.error("Notes update failed:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !request) {
    return (
      <div className="error-container">
        <p>{error || "Hiring request not found"}</p>
        <Button onClick={() => navigate("/hiring-requests")}>Back to List</Button>
      </div>
    );
  }

  return (
    <div className="hiring-request-detail">
      <header className="detail-header">
        <Button variant="secondary" onClick={() => navigate("/hiring-requests")}>
          <FiArrowLeft size={18} />
          Back
        </Button>
        <div className="header-actions">
          <Button variant="secondary" onClick={() => setShowStatusModal(true)}>
            <FiEdit size={18} />
            Update Status
          </Button>
          <Button variant="secondary" onClick={() => setShowNotesModal(true)}>
            <FiEdit size={18} />
            Edit Notes
          </Button>
        </div>
      </header>

      <div className="detail-content">
        <div className="detail-card">
          <h2>Company Information</h2>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Company Name</label>
              <p>{request.companyName}</p>
            </div>
            <div className="detail-field">
              <label>Contact Name</label>
              <p>{request.contactName}</p>
            </div>
            <div className="detail-field">
              <label>Email</label>
              <p>
                <FiMail size={16} /> {request.email}
              </p>
            </div>
            {request.phone && (
              <div className="detail-field">
                <label>Phone</label>
                <p>
                  <FiPhone size={16} /> {request.phone}
                </p>
              </div>
            )}
            <div className="detail-field">
              <label>Status</label>
              <StatusBadge status={request.status} />
            </div>
            <div className="detail-field">
              <label>Created</label>
              <p>
                <FiCalendar size={16} />{" "}
                {new Date(request.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h2>Hiring Details</h2>
          <div className="detail-grid">
            {request.role && (
              <div className="detail-field">
                <label>Role/Position</label>
                <p>{request.role}</p>
              </div>
            )}
            {request.hiringType && (
              <div className="detail-field">
                <label>Hiring Type</label>
                <p>{request.hiringType}</p>
              </div>
            )}
            {request.techStack && (
              <div className="detail-field full-width">
                <label>Tech Stack</label>
                <p>{request.techStack}</p>
              </div>
            )}
          </div>
        </div>

        {request.notes && (
          <div className="detail-card">
            <h2>Internal Notes</h2>
            <p className="notes-content">{request.notes}</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Status"
      >
        <Select
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          options={[
            { value: "new", label: "New" },
            { value: "in_review", label: "In Review" },
            { value: "contacted", label: "Contacted" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleStatusUpdate} disabled={updateLoading}>
            {updateLoading ? "Updating..." : "Update"}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Edit Notes"
      >
        <FormInput
          type="textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add internal notes..."
          rows={6}
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleNotesUpdate} disabled={updateLoading}>
            {updateLoading ? "Saving..." : "Save Notes"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default HiringRequestDetail;

